<script setup lang="ts">

import {useCurrentWorkspaceUser} from "~/stores/spaces";
import {formRules} from "~/utils/formRules";
import {addPlanToEnvironmentInvoiceBased} from "~/calls/subscription";
import {countries} from 'countries-list'
import {taxIdTypes} from "~/types/subscription.types";


const {workspaceOwnerEmail, isOwner} = useCurrentWorkspaceUser()
const {workspaceId, environmentId} = useEnvironment()
const {showUiNotification} = useUiNotifications()

const email = ref(workspaceOwnerEmail.value)
const complete = ref<any | null>(false)
const addressLine1 = ref<any | null>("")
const companyName = ref<any | null>("")
const addressLine2 = ref<any | null>("")
const postalCode = ref<any | null>("")
const city = ref<any | null>("")
const country = ref<any | null>(null)
const taxNumberType = ref<any | null>(null)
const taxNumber = ref<any | null>("")
const needTaxId = ref(false)
const accepted = ref(false)
const loading = ref(false)
const form = ref(null as any)
const formIsCompleted = ref(null)

const props = defineProps<{
  currency: string,
  planType: string,
  amount: number,
}>()

const emits = defineEmits(['close'])

const onSubmit = async () => {
  const validation = await form.value.validate()
  if (!validation.valid) {
    return
  }
  loading.value = true

  addPlanToEnvironmentInvoiceBased(workspaceId.value, environmentId.value, props.planType, props.amount, props.currency, {
    companyName: companyName.value,
    email: email.value,
    addressLine1: addressLine1.value,
    addressLine2: addressLine2.value,
    city: city.value,
    postalCode: postalCode.value,
    country: country.value,
    taxNumberType: needTaxId.value ? taxNumberType.value : null,
    taxNumber: needTaxId.value ? taxNumber.value : null,
  }).then((r) => {
    if (!r.success) return
    showUiNotification({
      type: 'success',
      message: 'The invoice has been sent to your specified email address. You can start enjoying AxonIQ Console Pro immediately.',
    })
    accepted.value = false
    complete.value = false
    emits('close')
  }).finally(() => {
    loading.value = false
  })
}


const formattedCountries = Object.keys(countries).map((c: string) => {
  return {
    value: c,
    title: countries[c]!!.name,
    continent: countries[c]!!.continent,
  }
})

const applicableTaxIdOptions = computed(() => {
  const selectedCountry = formattedCountries.find((c) => c.value === country.value)
  if (!selectedCountry) {
    return taxIdTypes
  }
  return taxIdTypes.filter((vat) => {
    return vat.value.startsWith(country.value.toLowerCase()) || vat.value.toLowerCase().startsWith(selectedCountry.continent.toLowerCase())
  })
})

watch(applicableTaxIdOptions, () => {
  if (applicableTaxIdOptions.value.length > 0) {
    taxNumberType.value = applicableTaxIdOptions.value[0].value
  } else {
    taxNumberType.value = null
  }
})

</script>
<template>
  <v-form v-model="formIsCompleted" ref="form" @submit.prevent="onSubmit">
    <v-alert class="my-4">
      <p>
        You can receive a monthly invoice for your subscription to be paid within 7 days of issuance.
        <template v-if="currency === 'USD'">
          You will be able to pay with credit card and PayPal. If you'd like to pay with bank transfer, please select
          Euros as currency.
        </template>
        <template v-if="currency === 'EUR'">
          You will be able to pay with bank transfer (swift), as well as credit card and PayPal.
        </template>
      </p>
    </v-alert>
    <div class="my-2">
      <p class="h5">
        Which email address should we send the invoice to?
      </p>

      <v-text-field v-model="email" label="Email address" type="text" required class="mt-2" hide-details
                    :rules="[formRules.validEmail]"/>
    </div>
    <div class="my-2">

      <p class="h5">
        To which company and address should the invoice be addressed?
      </p>
      <v-text-field v-model="companyName" label="Company name" type="text" required class="mt-2" hide-details
                    :rules="[formRules.required]"/>
      <v-text-field v-model="addressLine1" label="Address line 1" type="text" required class="mt-2" hide-details
                    :rules="[formRules.required]"/>
      <v-text-field v-model="addressLine2" label="Address line 2" type="text" class="mt-2" hide-details/>
      <v-text-field v-model="postalCode" label="Postal code" type="text" required class="mt-2" hide-details
                    :rules="[formRules.required]"/>
      <v-text-field v-model="city" label="City" type="text" required class="mt-2" hide-details
                    :rules="[formRules.required]"/>
      <v-select v-model="country" :items="formattedCountries" label="Country" required class="mt-2" hide-details
                :rules="[formRules.required]"/>

      <v-checkbox color="primary" v-model="needTaxId" hide-details label="I need a Tax ID on the invoice"/>

      <template v-if="needTaxId">
        <p class="h5 mt-2">
          Please supply the Tax ID for the invoice
        </p>

        <div class="d-flex gap-2">
          <v-select v-model="taxNumberType" :items="applicableTaxIdOptions.length ? applicableTaxIdOptions : taxIdTypes"
                    label="Tax ID" type="text" class="mt-2" hide-details  :rules="[formRules.required]"/>
          <v-text-field v-model="taxNumber" label="Tax ID" type="text" class="mt-2" hide-details  :rules="[formRules.required]"/>
        </div>
      </template>
    </div>
    <v-alert class="pa-0 my-4">

      <v-checkbox color="primary" v-model="accepted" :rules="[formRules.required]" class="my-2" hide-details label="
    I understand that an invoice will be sent to the email address I provided and have 7 days to pay the invoice, or
        the subscription will be canceled. I understand that I will receive a new invoice every month."/>
    </v-alert>


    <div class="d-flex justify-end">
      <v-btn color="primary" type="submit" :loading="loading"
             :disabled="!formIsCompleted || !accepted || !isOwner">
        Start subscription
      </v-btn>
    </div>
  </v-form>
</template>
